import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { InlinePromotionComponent } from '../../shared/components/inline-promotion/inline-promotion.component';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ParticlesComponent } from '../../shared/components/particles/particles.component';
@Component({
  selector: 'app-services',
  standalone: true,
  imports: [RouterLink, InlinePromotionComponent, ScrollAnimateDirective, ParticlesComponent],
  template: `
    <!-- Hero -->
    <section class="relative py-24 md:py-32 -mt-20 pt-32 md:pt-40 bg-surface-950 overflow-hidden grain-overlay">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-surface-950 to-brand-900"></div>
      <app-particles class="absolute inset-0 z-[1] pointer-events-none" [count]="50" [connectionDistance]="130" [mouseRadius]="180" />
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div class="max-w-2xl">
          <div class="section-label section-label-light">{{ page.hero.label }}</div>
          <h1 class="font-heading text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5">
            {{ page.hero.title }} <span class="text-brand-300">{{ page.hero.highlight }}</span>
          </h1>
          <p class="text-brand-200 leading-relaxed">{{ page.hero.description }}</p>
        </div>
      </div>
    </section>

    <!-- Services -->
    <section class="py-20 md:py-28 bg-white">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        @for (service of page.services; track service.title; let i = $index; let odd = $odd) {
          <div appScrollAnimate data-animate
               class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20 last:mb-0"
               [class]="odd ? 'lg:[direction:rtl] lg:[&>*]:[direction:ltr]' : ''">
            <a [routerLink]="'/services/' + service.slug" class="aspect-[4/3] rounded-xl overflow-hidden relative group/img block">
              @if (service.image) {
                <img [src]="service.image" [alt]="service.title"
                     class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover/img:scale-105" />
              } @else {
                <div class="w-full h-full flex items-center justify-center" [style.background]="service.gradient">
                  <span class="text-6xl opacity-20" [innerHTML]="service.icon"></span>
                </div>
              }
            </a>
            <div>
              <div class="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-brand-50 text-brand-600 mb-4">
                <span class="text-xl" [innerHTML]="service.icon"></span>
              </div>
              <a [routerLink]="'/services/' + service.slug" class="block">
                <h2 class="font-heading text-2xl md:text-3xl font-bold text-surface-900 tracking-tight mb-4 hover:text-brand-600 transition-colors">{{ service.title }}</h2>
              </a>
              <p class="text-surface-500 leading-relaxed mb-5">{{ service.desc }}</p>
              <ul class="space-y-2.5 mb-6">
                @for (f of service.features; track f) {
                  <li class="flex items-center gap-2.5 text-sm text-surface-600">
                    <svg aria-hidden="true" class="w-4 h-4 text-brand-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                    {{ f }}
                  </li>
                }
              </ul>
              <div class="flex flex-wrap items-center gap-4">
                <a [routerLink]="'/services/' + service.slug" class="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white font-semibold text-sm rounded-lg hover:bg-brand-700 transition-colors">
                  Learn More
                  <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
                <a routerLink="/contact" class="group inline-flex items-center gap-2 text-sm font-semibold text-surface-500 hover:text-brand-600 transition-colors">
                  Request a Quote
                  <svg aria-hidden="true" class="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </a>
              </div>
            </div>
          </div>
        }
      </div>
    </section>

    <!-- Promotion -->
    @if (servicesPromo) {
      <app-inline-promotion [promo]="servicesPromo" />
    }

    <!-- Process -->
    <section class="py-20 md:py-28 bg-surface-50">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-14" appScrollAnimate data-animate>
          <div class="section-label justify-center">How We Work</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-tight">Our Process</h2>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          @for (step of page.process; track step.title; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i+1)*100"
                 class="text-center p-6 rounded-xl bg-white border border-surface-100">
              <div class="w-12 h-12 rounded-full bg-brand-600 text-white flex items-center justify-center mx-auto mb-4 font-heading text-lg font-bold">{{ i + 1 }}</div>
              <h3 class="font-heading text-base font-bold text-surface-900 mb-2">{{ step.title }}</h3>
              <p class="text-sm text-surface-500 leading-relaxed">{{ step.desc }}</p>
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class ServicesComponent implements OnInit {
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  page = this.config.pages.services;
  servicesPromo = this.config.promotions.servicesInline;

  ngOnInit(): void {
    const serviceSchemas = this.page.services.map(s =>
      this.seo.getServiceSchema(s.title, s.desc)
    );
    this.seo.setJsonLd([
      this.seo.getBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Services', url: '/services' },
      ]),
      ...serviceSchemas,
    ]);
  }
}
